import { useEffect, useState } from "react";
import { Loader2, Mail, RefreshCw, Send, Star, X } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/apiAdmin";
import { ReviewStripSettings } from "./ReviewStripSettings";

const inp = "w-full rounded-xl px-3 py-2 text-sm bg-white/[0.03] border border-white/10 text-white focus:border-sky-500 outline-none";
const err = (e) => toast.error(e?.response?.data?.detail || "Failed");

const Reader = ({ id, onClose }) => {
  const [m, setM] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiGet(`/admin/gmail/message/${id}`).then(setM).catch((e) => { err(e); onClose(); }); }, [id, onClose]);
  const send = () => {
    const to = (m.from.match(/<([^>]+)>/) || [null, m.from])[1];
    setBusy(true);
    apiPost("/admin/gmail/send", { to, subject: m.subject.startsWith("Re:") ? m.subject : `Re: ${m.subject}`, body: reply, thread_id: m.thread_id, reply_to_id: m.message_id })
      .then(() => { toast.success("Reply sent from your Gmail"); onClose(); }).catch(err).finally(() => setBusy(false));
  };
  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-2 sm:p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="gmail-reader">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        {!m ? <Loader2 className="w-5 h-5 animate-spin text-sky-500" /> : <>
          <p className="text-white font-['Outfit'] text-lg pr-8">{m.subject}</p>
          <p className="text-xs text-gray-500">{m.from} · {m.date}</p>
          <pre className="whitespace-pre-wrap text-sm text-gray-300 font-sans max-h-72 overflow-y-auto">{m.body || m.snippet}</pre>
          <textarea rows={4} className={inp} placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="gmail-reply-text" />
          <button onClick={send} disabled={busy || !reply.trim()} className="h-10 px-5 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white text-sm flex items-center gap-2" data-testid="gmail-reply-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send reply
          </button>
        </>}
      </div>
    </div>
  );
};

const Inbox = () => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const load = () => { setItems(null); apiGet("/admin/gmail/inbox").then((d) => { setItems(d.items); setError(""); }).catch((e) => { setItems([]); setError(e?.response?.data?.detail || "Gmail unavailable"); }); };
  useEffect(load, []);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-3" data-testid="gmail-inbox">
      <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-sky-400" /><p className="text-white font-['Outfit'] flex-1">Gmail — last 7 days</p>
        <button onClick={load} className="p-1.5 text-gray-400 hover:text-white" data-testid="gmail-refresh"><RefreshCw className="w-4 h-4" /></button></div>
      {error && <p className="text-sm text-amber-300" data-testid="gmail-error">{error}</p>}
      {!items ? <Loader2 className="w-5 h-5 animate-spin text-sky-500" /> : (
        <div className="divide-y divide-white/5">{items.map((m) => (
          <button key={m.id} onClick={() => setOpen(m.id)} className="w-full text-left py-2.5 flex gap-3 hover:bg-white/[0.02]" data-testid={`gmail-item-${m.id}`}>
            <span className={`w-2 h-2 rounded-full mt-2 shrink-0 ${m.unread ? "bg-sky-400" : "bg-transparent"}`} />
            <span className="flex-1 min-w-0"><span className="block text-sm text-white truncate">{m.from.replace(/<.*>/, "")} — {m.subject}</span><span className="block text-xs text-gray-500 truncate">{m.snippet}</span></span>
          </button>
        ))}{!error && items.length === 0 && <p className="text-sm text-gray-500 py-4">Nothing new.</p>}</div>
      )}
      <p className="text-[11px] text-gray-500">Tip: ask JARVIS "any customer emails?" or "reply to John and say we can do Saturday" — it reads you the draft and only sends after you say yes.</p>
      {open && <Reader id={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const ReviewRow = ({ r, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = () => { setBusy(true); apiPost(`/admin/google-reviews/${r.review_id}/reply`, { text }).then(() => { toast.success("Reply posted on Google"); onDone(); }).catch(err).finally(() => setBusy(false)); };
  return (
    <div className="py-3 space-y-2" data-testid={`gbp-review-${r.review_id}`}>
      <div className="flex items-center gap-2 text-sm"><span className="text-white">{r.author_name}</span><span className="flex">{[...Array(r.rating)].map((_, i) => <Star key={i} className="w-3 h-3 fill-sky-500 text-sky-500" />)}</span></div>
      {r.text && <p className="text-sm text-gray-400">{r.text}</p>}
      {r.reply ? <p className="text-xs text-emerald-300 pl-3 border-l border-emerald-400/40">You replied: {r.reply}</p> : r.source === "gbp" && (
        <div className="flex gap-2"><input className={inp} placeholder="Write a public reply…" value={text} onChange={(e) => setText(e.target.value)} data-testid={`gbp-reply-input-${r.review_id}`} />
          <button onClick={send} disabled={busy || !text.trim()} className="px-4 rounded-xl bg-sky-500 text-white text-sm disabled:opacity-40" data-testid={`gbp-reply-send-${r.review_id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reply"}</button></div>
      )}
    </div>
  );
};

const Reviews = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => apiGet("/admin/google-reviews").then((d) => setItems(d.items.filter((x) => x.review_id !== "g_business_photos"))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const sync = () => {
    setBusy(true);
    apiPost("/admin/google-reviews/sync-business", {}).then((r) => { toast.success(`Synced ${r.count} Google reviews`); load(); })
      .catch((e) => apiPost("/admin/google-reviews/refresh", {}).then((r) => { toast.message(`${e?.response?.data?.detail || "Business Profile not available"} — ${r.ok ? `loaded ${r.count ?? ""} newest reviews via Places` : r.reason}`); load(); }))
      .finally(() => setBusy(false));
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-2" data-testid="gbp-reviews">
      <div className="flex items-center gap-2"><Star className="w-4 h-4 text-sky-400" /><p className="text-white font-['Outfit'] flex-1">Google reviews (live on your site, refreshed hourly)</p>
        <button onClick={sync} disabled={busy} className="h-8 px-3 rounded-lg border border-white/10 text-xs text-gray-300 hover:text-white flex items-center gap-1" data-testid="gbp-sync">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Sync now</button></div>
      {!items ? <Loader2 className="w-5 h-5 animate-spin text-sky-500" /> : items.length === 0 ? <p className="text-sm text-gray-500" data-testid="gbp-empty">No Google reviews loaded yet — connect Google or add your Places API key, then Sync now.</p>
        : <div className="divide-y divide-white/5">{items.map((r) => <ReviewRow key={r.review_id} r={r} onDone={load} />)}</div>}
    </div>
  );
};

export const GoogleHub = () => (
  <div className="grid lg:grid-cols-2 gap-6" data-testid="google-hub"><Inbox /><Reviews /><ReviewStripSettings /></div>
);
